/**
 * Launch-flow VIEW trace — the web half of Unity's LaunchFlowTrace
 * (Assets/Scripts/Core/Application/LaunchFlowTrace.cs).
 *
 * The startup flashing the owner reported (20260824) crosses the WebView boundary in both
 * directions: Unity paints a splash and a loading screen, then a native WebView slams over the top
 * of all of it and this app paints its own views inside that. A trace that stops at the boundary
 * cannot say which side any given frame came from — so both sides emit the SAME
 * `[LAUNCH VIEW] +Nms  VERB  name  detail` line shape, and these go out through `diagLog`, which
 * forwards to the host. In embedded mode the two traces therefore interleave in ONE Xcode / macOS
 * console log, in real order. The host re-prefixes ours as `[WebApp:LAUNCH VIEW]`, so one grep for
 * "LAUNCH VIEW" still pulls the whole cross-process sequence out of a device log.
 *
 * BOTH embedded hosts have to answer `diagLog` for that to hold, and until 20260824 only one did:
 * TimelineWebViewPanel handled it, PurchasingWebViewPopupPanel did not — and the purchasing panel is
 * the host for First Run, Sign In, Purchasing AND the Projects home, i.e. every view this trace was
 * written for. The handler was added there in the same change. Any FUTURE WebView host needs its own
 * branch or its half of the trace is silently dropped.
 *
 * Standalone (no bridge) they land in the browser console only, which is the whole trace there.
 *
 * Timings are per-WEBVIEW, from this module's first evaluation — NOT Unity's t0. A WebView is
 * created well after the app launches, so the two clocks cannot be compared directly; line ORDER is
 * what carries across, and that is what the report is about.
 *
 * TEMPORARY diagnostic aid, on by default alongside Unity's DiagLogLaunchFlow. Flip LAUNCH_TRACE
 * false to silence the web half on its own.
 */
import { useEffect, useRef } from 'react';
import { diagLog } from '@/bridge/transport';

/**
 * Matches ApplicationFeatureControl.DiagLogLaunchFlow. Off = every call below is a no-op.
 *
 * 20260824 - OFF for the TestFlight build, in step with the Unity half. These two switches are NOT wired
 * to each other, so they have to be flipped together by hand: leaving this one true would keep the WebView
 * pushing diagLog messages across the bridge on every launch while Unity's half is silent. Note the
 * asymmetry when re-enabling — the Unity const takes effect on a normal build, but this one needs
 * Tools > Build Timeline WebApp to reach the embedded bundle.
 */
export const LAUNCH_TRACE = false;

const t0 = typeof performance !== 'undefined' ? performance.now() : 0;

function elapsed(): number {
  return Math.round((typeof performance !== 'undefined' ? performance.now() : 0) - t0);
}

function emit(verb: string, view: string, detail?: string): void {
  if (!LAUNCH_TRACE) return;
  diagLog('LAUNCH VIEW', `+${elapsed()}ms  ${verb} web:${view}${detail ? `  ${detail}` : ''}`);
}

/** A view became visible. */
export function traceViewShown(view: string, detail?: string): void {
  emit('SHOW ', view, detail);
}

/** A view stopped being visible. */
export function traceViewHidden(view: string, detail?: string): void {
  emit('HIDE ', view, detail);
}

/** Something changed without appearing or disappearing (a route swap inside a view, a mode flip). */
export function traceViewNote(view: string, detail?: string): void {
  emit('NOTE ', view, detail);
}

/**
 * Mount/unmount pair for one view.
 *
 * Deps are EMPTY and `detail` is snapshotted in a ref at mount — deliberately, not by oversight.
 * `detail` is usually a template string rebuilt on every render, so keying the effect on it would
 * print a SHOW/HIDE pair every render for a view that never moved, turning the one trace meant to
 * establish ordering into noise.
 *
 * Under React StrictMode (dev only) effects double-invoke, so a mount prints SHOW/HIDE/SHOW. That
 * happens in the browser console and never on device, where the production build runs.
 */
export function useLaunchViewTrace(view: string, detail?: string): void {
  const detailRef = useRef(detail);
  useEffect(() => {
    traceViewShown(view, detailRef.current);
    return () => traceViewHidden(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
